import {Component, EventEmitter, Inject, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Master} from '../../../../admin/models/master.interface';
import {QuestionsService} from '../../../services/questions.service';
import {Questions} from '../../../models/questions.interface';

@Component({
  selector: 'app-edit-questions',
  templateUrl: './edit-questions.component.html',
  styleUrls: ['./edit-questions.component.scss']
})
export class EditQuestionsComponent implements OnInit {

  questionTypes = [
    {value: 'COUNTRY', description: 'Country'},
    {value: 'SYSTEM', description: 'System'},
    {value: 'BOTH', description: 'Both'},
  ];

  sharingLevel = [
    {value: 'UNDISCLOSED', description: 'Undisclosed'},
    {value: 'MEMBERS', description: 'Members'},
    {value: 'PUBLIC', description: 'Public'},
  ];

  answerTypes = [
    {value: 'TEXT', description: 'Text'},
    {value: 'NUMBER', description: 'Number'},
    {value: 'BOOLEAN', description: 'Boolean'},
    {value: 'LIST', description: 'List'},
  ];

  charts = [
    {value: 'visible-fee', description: 'Use of visible fee'},
    {value: 're-use-targets', description: 'Countries with re-use targets'},
    {value: 'e-waste-legislation', description: 'Categories in scope of e-waste legislation'},
    {value: 'weee-treatment-standard-mandatory', description: 'Is there a WEEE treatment standard mandatory?'},
    {value: 'country-retail-vs-municipalities-vs-other', description: 'Country % of WEEE collected from retail vs municipalities vs other'},
    {value: 'weee-system-retail-vs-municipalities-vs-other', description: 'WEEE System % of WEEE collected from retail vs municipalities vs other'},
    {value: 'collection-points-operated', description: 'Collection points serviced by WF members'},
    {value: 'employees-per-pro', description: 'Employees per PRO'},
    {value: 'recycling-facilities', description: 'Network of recycling facilities per PRO'},
    {value: 'members-adhered', description: 'Number of members adhered'},
    {value: 'systems-map', description: 'Number of systems in the country'}
  ];

  questionEditForm = this.formBuilder.group({
    shortCode: ['', Validators.required],
    description: ['', Validators.required],
    label: [''],
    category: [1, Validators.required],
    yearly: [{value: false, disabled: true }],
    questionType: [{value: '', disabled: true }, Validators.required],
    atAGlanceInfo: [false],
    sharingLevelLocked: [false],
    sharingLevel: ['', Validators.required],
    answerType: [{value: '', disabled: true }, Validators.required],
    maxLength: [0, Validators.required],
    minValue: [0, Validators.required],
    maxValue: [0, Validators.required],
    availableAnswers: [''],
    multiSelect: [false],
    mandatory: [{value: false}],
    comment: [''],
    chart: [],
  });

  get shortCodeControl(): FormControl {
    return this.questionEditForm.get('shortCode') as FormControl;
  }
  get descriptionControl(): FormControl {
    return this.questionEditForm.get('description') as FormControl;
  }
  get labelControl(): FormControl {
    return this.questionEditForm.get('label') as FormControl;
  }
  get categoryControl(): FormControl {
    return this.questionEditForm.get('category') as FormControl;
  }
  get yearlyControl(): FormControl {
    return this.questionEditForm.get('yearly') as FormControl;
  }
  get questionTypeControl(): FormControl {
    return this.questionEditForm.get('questionType') as FormControl;
  }
  get atAGlanceInfoControl(): FormControl {
    return this.questionEditForm.get('atAGlanceInfo') as FormControl;
  }
  get sharingLevelLockedControl(): FormControl {
    return this.questionEditForm.get('sharingLevelLocked') as FormControl;
  }
  get sharingLevelControl(): FormControl {
    return this.questionEditForm.get('sharingLevel') as FormControl;
  }
  get answerTypeControl(): FormControl {
    return this.questionEditForm.get('answerType') as FormControl;
  }
  get maxLengthControl(): FormControl {
    return this.questionEditForm.get('maxLength') as FormControl;
  }
  get minValueControl(): FormControl {
    return this.questionEditForm.get('minValue') as FormControl;
  }
  get maxValueControl(): FormControl {
    return this.questionEditForm.get('maxValue') as FormControl;
  }
  get availableAnswersControl(): FormControl {
    return this.questionEditForm.get('availableAnswers') as FormControl;
  }
  get multiSelectControl(): FormControl {
    return this.questionEditForm.get('multiSelect') as FormControl;
  }
  get mandatoryControl(): FormControl {
    return this.questionEditForm.get('mandatory') as FormControl;
  }
  get commentControl(): FormControl {
    return this.questionEditForm.get('comment') as FormControl;
  }
  get chartControl(): FormControl {
    return this.questionEditForm.get('chart') as FormControl;
  }

  @Output()
  editEvent: EventEmitter<void> = new EventEmitter<void>();

  constructor(private dialogRef: MatDialogRef<EditQuestionsComponent>,
              @Inject(MAT_DIALOG_DATA) public data: { question: Questions, categories: Master[] },
              private readonly formBuilder: FormBuilder,
              private readonly questionsService: QuestionsService) { }

  ngOnInit(): void {
    this.shortCodeControl.setValue(this.data.question.shortCode);
    this.descriptionControl.setValue(this.data.question.description);
    this.labelControl.setValue(this.data.question.label);
    this.categoryControl.setValue(this.data.question.category.id);
    this.yearlyControl.setValue(this.data.question.yearly);
    this.questionTypeControl.setValue(this.data.question.questionType);
    this.atAGlanceInfoControl.setValue(this.data.question.atAGlanceInfo);
    this.sharingLevelLockedControl.setValue(this.data.question.sharingLevelLocked);
    this.sharingLevelControl.setValue(this.data.question.sharingLevel);
    this.answerTypeControl.setValue(this.data.question.answerType);

    const answerFormat = JSON.parse(this.data.question.answerFormatString);
    switch (this.data.question.answerType) {
      case 'TEXT':
        this.maxLengthControl.setValue(answerFormat.MaxLength);
        break;
      case 'NUMBER':
        this.maxValueControl.setValue(answerFormat.Max);
        this.minValueControl.setValue(answerFormat.Min);
        break;
      case 'BOOLEAN':
        break;
      case 'LIST':
        let list = '';
        answerFormat.AvailableAnswers.forEach((value: string) => {
          list += value + '\n';
        });

        this.availableAnswersControl.setValue(list);
        this.multiSelectControl.setValue(this.data.question.multiSelect);
        break;
    }

    this.mandatoryControl.setValue(this.data.question.mandatory);
    this.commentControl.setValue(this.data.question.comment);
    this.chartControl.setValue(this.data.question.chart);

    this.setMaxValueMinimumLimit();
  }

  sendEditRequest(): void {
    const dto: Questions | null = this.getEditDto();
    if (dto) {
      this.questionsService.editQuestion(dto).subscribe(
        () => {
          this.editEvent.emit();
          this.closeDialog();
        },
        () => {
          this.closeDialog();
        }
      );
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  setMaxValueMinimumLimit(): void {
    this.questionEditForm.get('maxValue')?.setValidators(Validators.min(this.minValueControl.value));
    this.questionEditForm.get('maxValue')?.updateValueAndValidity();
  }

  private getEditDto(): Questions {
    let category: Master = {code: '', comment: '', description: '', disabled: false, id: 0};
    this.data.categories.forEach(cat => {
      if (cat.id === this.categoryControl.value) {
        category = cat;
      }
    });
    let answerFormat = {};

    switch (this.answerTypeControl.value) {
      case 'TEXT':
        answerFormat = {MaxLength: this.maxLengthControl.value};
        break;
      case 'NUMBER':
        answerFormat = {Min: this.minValueControl.value, Max: this.maxValueControl.value};
        break;
      case 'BOOLEAN':
        answerFormat = {Value: false};
        break;
      case 'LIST':
        answerFormat = {AvailableAnswers: this.availableAnswersControl.value.split('\n')};
        break;
    }
    return {
      id: this.data.question.id,
      shortCode: this.shortCodeControl.value,
      description: this.descriptionControl.value,
      label: this.labelControl.value,
      category,
      yearly: this.yearlyControl.value,
      questionType: this.questionTypeControl.value,
      atAGlanceInfo: this.atAGlanceInfoControl.value,
      sharingLevel: this.sharingLevelControl.value,
      sharingLevelLocked: this.sharingLevelLockedControl.value,
      answerType: this.answerTypeControl.value,
      answerFormatString: JSON.stringify(answerFormat),
      mandatory: this.mandatoryControl.value,
      multiSelect: this.multiSelectControl.value,
      comment: this.commentControl.value,
      chart: this.chartControl.value,
      status: this.data.question.status
    };
  }

}
